import React, { use, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import CustomSelect from "components/Common/custom-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
 import { useDispatch, useSelector } from "react-redux";
import { fetchActiveClient, fetchEmployeeDropdownAutoList, getProjectServiceList, getProjectServiceMappingList, saveProjectServiceMapping } from "store/actions";
import useButtonAccess from "components/Common/useButtonAccess";
 
const ProjectServiceMap = () => {
  const [formErrors, setFormErrors] = useState({ client: false, project: false });
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);

  const { 
      project, 
      clientActiveList,
      clientProjectService,
      mappingList,
      projectServiceList,
      dropdownAutoList,
      loading,
      success,
      error, 
  } = useSelector((state) => ({
      project : state.TimesheetSetupProjectReducer?.project  ?? [],
      clientActiveList : state.TimesheetSetupClientReducer?.clientActiveList  ?? [], 
      clientProjectService : state.TimesheetSetupProjectReducer?.clientProjectService  ?? [],
      mappingList : state.TimesheetSetupProjectReducer?.mappingList  ?? [],
      projectServiceList : state.TimesheetSetupProjectReducer?.projectServiceList  ?? [],
      dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],

      loading: state.TimesheetSetupProjectReducer?.loading,
      error: state.TimesheetSetupProjectReducer?.error,
      success: state.TimesheetSetupProjectReducer?.success,
  }));

    
  useEffect(() => {
      const authUserDetails = useAuthUserDetails();
      setAuthDetails(authUserDetails[0])
      const unitId = authUserDetails[0].unit_id         
      dispatch(fetchActiveClient(unitId));      
      dispatch(fetchEmployeeDropdownAutoList(unitId))
  }, [dispatch]); 

  useEffect(() => {
    if (Array.isArray(mappingList) && mappingList.length > 0) {
      const projects = mappingList.map(item => ({
        value: item.project_id,
        label: item.project,
      }));

      setProjectOptions(projects);
    } else {
      setProjectOptions([]);
    }
  }, [mappingList]);
 
  const userOptions = React.useMemo(() => {
    return dropdownAutoList.map(emp => ({
      value: String(emp.emp_id),
      label: emp.emp_no          
    }));
  }, [dropdownAutoList]);

 
 
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
 
 
  const [tableData, setTableData] = useState([]);
 
  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState({});

  const formatTimeWithColon = (val) => {
    const str = String(val ?? "");   // ✅ force string

    const digits = str.replace(/\D/g, "").slice(0, 5);

    if (digits.length <= 2) return digits;
    if (digits.length === 3) return digits;

    const hoursLength = digits.length === 4 ? 2 : 3;
    return `${digits.slice(0, hoursLength)}:${digits.slice(hoursLength)}`;
  };


  const normalizeHours = (input) => {
    const str = String(input ?? "");   // ✅ force string

    if (!str) return "";

    const digits = str.replace(/\D/g, "").slice(0, 5);

    if (digits.length === 1) return `0${digits}:00`;
    if (digits.length === 2) return `${digits}:00`;
    if (digits.length === 3) return `${digits}:00`;

    if (digits.length === 4) {
      const hours = digits.slice(0, 2);
      const minutes = Math.min(+digits.slice(2), 59)
        .toString()
        .padStart(2, "0");
      return `${hours}:${minutes}`;
    }

    const hours = digits.slice(0, 3);
    const minutes = Math.min(+digits.slice(3), 59)
      .toString()
      .padStart(2, "0");

    return `${hours}:${minutes}`;
  };
 
  React.useEffect(() => {
    if (Array.isArray(projectServiceList) && projectServiceList.length) {
      const rows = projectServiceList.map(item => ({
        id: item.service_id,
        service: item.service_name,
        assigned_to: item.assign_id ? String(item.assign_id) : "",
        total_hours: item.assign_hours
          ? normalizeHours(item.assign_hours)
          : ""
      }));

      setTableData(rows);
    } else {
      setTableData([]);
    }
  }, [projectServiceList]);

  React.useEffect(() => {
    if (Array.isArray(tableData) && tableData.length) {
      formik.setFieldValue(
        "rows",
        tableData.map(row => ({
          id: row.id,
          assigned_to: row.assigned_to || "",
          total_hours: row.total_hours || ""
        }))
      );
    } else {
      formik.setFieldValue("rows", []);
    }
  }, [tableData]);


  const formik = useFormik({
    initialValues: {
      rows: [],
    },

    validationSchema: Yup.object({
      rows: Yup.array().of(
        Yup.object({
          assigned_to: Yup.string()
            .trim()
            .required("Assigned To is required"),

          total_hours: Yup.string()
            .trim()
            .required("Total Hours is required")
            .matches(
              /^(?:\d{1,3}):[0-5]\d$/,
              "Enter valid time (max 999:59)"
            )
        })
      )
    }),

    onSubmit: values => {
      // console.log(values.rows);

      const servicesPayload = values.rows.map(row => ({
        service_id: Number(row.id),                // Convert string to number
        assign_id: Number(row.assigned_to),        // Convert string to number
        assignHours: Number(row.total_hours.split(":")[0]) // Only hours as integer
      }));

      const payload = new FormData();
      payload.append("client_id", Number(formik.values.client?.value ?? 0));
      payload.append("project_id", Number(selectedProject?.value ?? 0));
      payload.append("unit_id", Number(authDetails?.unit_id ?? 0));
      payload.append("created_by", authDetails?.user_id ?? ""); 
      payload.append("ip_address", "");
      payload.append("skill_map_id", 0);
      payload.append("services", JSON.stringify(servicesPayload));
      dispatch(saveProjectServiceMapping(payload));

    },
  });

  useEffect(() => {
    if (success) {
      const payload = new FormData();
      payload.append("clientId", formik.values.client?.value ?? "");
      payload.append("projectId", selectedProject?.value ?? "");
      payload.append("unitId", authDetails?.unit_id ?? "");

      dispatch(getProjectServiceList(payload));
    }
  }, [authDetails, success, dispatch]);

  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => tableData.length - row.index,
      className: "serial-no text-center",
      applyHeaderClass: true,
    },

    { 
      Header: "Services", 
      accessor: "service" 
    },

    {
      Header: (
        <>
          Assigned To <span className="text-danger">*</span>
        </>
      ),
      id: "assigned_to",
      className: "desc-select-col",
      applyHeaderClass: true,
      Cell: ({ row }) => {
        const index = row.index;
        const error =
          formik.touched.rows?.[index]?.assigned_to &&
          formik.errors.rows?.[index]?.assigned_to;

        const selectedOption =
          userOptions.find(
            opt => opt.value === formik.values.rows[index]?.assigned_to
          ) || null;

        return (
          <>
            <CustomSelect
              options={userOptions}
              value={selectedOption}
              onChange={(val) => {
                handleRowChange(index, "assigned_to", val?.value || "");
                formik.setFieldTouched(`rows[${index}].assigned_to`, true);
              }}
              onBlur={() =>
                formik.setFieldTouched(`rows[${index}].assigned_to`, true)
              }
              className={error ? "is-invalid" : ""}
            />
            {error && <div className="text-danger small">{error}</div>}
          </>
        );
      }
    }
    ,{
      Header: (
        <>
          Total Hours <span className="text-danger">*</span>
        </>
      ),
      id: "total_hours",
      className: "action-col",
      applyHeaderClass: true,
      Cell: ({ row }) => {
        const index = row.index;

        const [localValue, setLocalValue] = React.useState(
          formik.values.rows[index]?.total_hours || ""
        );

        React.useEffect(() => {
          setLocalValue(formik.values.rows[index]?.total_hours || "");
        }, [formik.values.rows[index]?.total_hours]);

        const error =
          formik.touched.rows?.[index]?.total_hours &&
          formik.errors.rows?.[index]?.total_hours;

        return (
          <>
            <Input
              type="text"
              className={`form-control ${error ? "is-invalid" : ""}`}
              style={{ width: "95px" }}
              placeholder="HHH:mm"
              value={localValue}
              onChange={(e) =>
                setLocalValue(formatTimeWithColon(e.target.value))
              }
              onBlur={() => {
                const normalized = normalizeHours(localValue);
                setLocalValue(normalized);
                handleRowChange(index, "total_hours", normalized);
                formik.setFieldTouched(`rows[${index}].total_hours`, true);
              }}
            />
            {error && <div className="text-danger small">{error}</div>}
          </>
        );
      }
    }

  ];

  const handleRowChange = (index, field, value) => {
    formik.setFieldValue(`rows[${index}].${field}`, value);
  };

  
  
  const validateForm = () => {
    const errors = {
      client: false,
      project: false,
    };

    if (!formik.values.client) {
      errors.client = true;
    }

    if (!selectedProject) {
      errors.project = true;
    }

    setFormErrors(errors);

    return !errors.client && !errors.project;
  };

 
 
  const handleFormAction = (e, action) => {
      e.preventDefault();

      if (action === "cancel") {
        // Reset everything
        setSelectedClient(null);
        setSelectedProject(null);
        setProjectOptions([]);
        setFormErrors({ client: false, project: false });
        setTableData([]);
        formik.resetForm();
        return;
      }

      // Validate before search
      if (!validateForm()) return;

      // Search payload
      const payload = new FormData();
      payload.append("clientId", formik.values.client?.value ?? "");
      payload.append("projectId", selectedProject?.value ?? "");
      payload.append("unitId", authDetails?.unit_id ?? "");

      dispatch(getProjectServiceList(payload));
    };

 
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Timesheet"
          breadcrumbItem="Project Service Map"
        />
 
 
        <Card>
          <CardBody>
            <Form>
              <Row className="align-items-center">
                <Col md={4}>
                  <FormGroup>
                    <Label>Client <span className="text-danger">*</span></Label>
                    <CustomSelect
                      id="client"
                      name="client"
                      isMulti={false}
                      options={Array.isArray(clientActiveList) ? clientActiveList : []}
                      value={formik.values.client}
                      onChange={(selectedOption) => {
                        // 1️⃣ Set Formik client
                        formik.setFieldValue("client", selectedOption);

                        // 2️⃣ Reset project selection
                        setSelectedProject(null);
                        setProjectOptions([]);
                        
                        // 3️⃣ Reset table data
                        setTableData([]);
                        formik.setFieldValue("rows", []);

                        // 4️⃣ Fetch project list for selected client
                        const formData = new FormData();
                        formData.append("clientId", selectedOption?.value ?? "");
                        formData.append("unitId", authDetails?.unit_id ?? "");
                        dispatch(getProjectServiceMappingList(formData));
                      }}
                      onBlur={() => formik.setFieldTouched("client", true)}
                    />
                    {formErrors.client && (
                      <div className="text-danger">Client is required</div>
                    )}
                  </FormGroup>
                </Col>

 
                <Col md={4}>
                  <FormGroup>
                    <Label>Project <span className="text-danger">*</span></Label>
                    <CustomSelect
                      options={projectOptions}
                      value={selectedProject}
                      onChange={(val) => {
                        setSelectedProject(val);
                        setFormErrors({ ...formErrors, project: false });

                        // ✅ Reset table data when project changes
                        setTableData([]);
                        formik.setFieldValue("rows", []);
                      }}
                    />
                    {formErrors.project && (
                      <div className="text-danger">Project is required</div>
                    )}
                  </FormGroup>
                </Col>

 
                <Col className="d-flex mob-center align-items-center justify-content-end mt-2"> 
                  <Button className="btn button-search me-2" onClick={e => handleFormAction(e, 'search')}>
                    <i className="fas fa-search me-1" /> Search
                  </Button>
                  {tableData && tableData.length > 0 && (canCreate || canEdit) && (
                    <Button className="btn button-save me-2" onClick={formik.handleSubmit}>
                      <i className="fas fa-save me-1" /> Save
                    </Button>
                  )}
                  <Button className="btn button-cancel me-2" onClick={e => handleFormAction(e, 'cancel')}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
 
       
        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={tableData}
              isGlobalFilter
              customPageSize={10}
              disableSortBy={true}  
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};
 
export default ProjectServiceMap;